import { type DialogProps } from "@mui/material";

import { yupResolver } from "@hookform/resolvers/yup";
import { ProductType } from "@repo/api";
import { AMLFormDefaultValues, type AMLFormValues } from "@repo/logic";
import { AMLQuestionsForm, AMLQuestionsSchema, cs, mapAMLRequest } from "@repo/ui";
import { useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { usePrimaryAPIClient } from "../../../context/PrimaryAPIClient";
import { useDrawer } from "../../Drawer/context";
import FormMultiStepDrawer from "../../Drawer/FormMultiStepDrawer/index";
import {
    mapFetchApiErrorToSubmitStatus,
    SubmitStatus,
    SubmitStatusStep,
} from "../../Drawer/GlobalSteps/SubmitStatusStep";
import Modal from "../../Drawer/Modal";
import { TriggerButton } from "../../Link";

type IAMLDrawer = {
    contractId: string;
    contractType: string;
    onCloseCallback?: () => void;
};

export const AMLForm = ({ contractId, contractType, onCloseCallback }: IAMLDrawer) => {
    const { nextStep } = useDrawer();
    const { contractsDpsApi, contractsPpApi } = usePrimaryAPIClient();
    const [status, setStatus] = useState<`${SubmitStatus}`>(SubmitStatus.Idle);

    const methods = useForm<AMLFormValues>({
        defaultValues: AMLFormDefaultValues,
        resolver: yupResolver(AMLQuestionsSchema) as any,
        shouldFocusError: false,
    });

    const handleFormSubmit = async () => {
        await methods.handleSubmit(async (values) => {
            try {
                setStatus(SubmitStatus.Submitting);
                if (contractType === ProductType.Uf) {
                    await contractsDpsApi.setDPSAmlIdentificationPut({
                        contractId,
                        setAmlIdentificationRequest: mapAMLRequest(values),
                    });
                } else {
                    await contractsPpApi.setPPAmlIdentificationPut({
                        contractId,
                        setAmlIdentificationRequest: mapAMLRequest(values),
                    });
                }
                setStatus(SubmitStatus.Success);
                nextStep();
            } catch (error) {
                setStatus(mapFetchApiErrorToSubmitStatus(error));
                nextStep();
            }
            onCloseCallback?.();
        })();
    };

    return (
        <FormProvider {...methods}>
            <FormMultiStepDrawer
                title={cs.AML.amlQuestionaire.title}
                onCloseCallback={onCloseCallback}
                isSubmitting={methods.formState.isSubmitting}
                formStatus={status}
            >
                {[
                    {
                        component: (
                            <AMLQuestionsForm
                                key="aml-questions"
                                helpText={cs.AML.amlReasoning.insurancePayout}
                                autoScrollFirstError={false}
                            />
                        ),
                        stepName: "aml-questions",
                        stepConfig: {
                            customNextButton: (
                                <TriggerButton onClick={() => void handleFormSubmit()}>
                                    {cs.AML.amlQuestionaire.save}
                                </TriggerButton>
                            ),
                        },
                    },
                    {
                        component: (
                            <SubmitStatusStep
                                status={status}
                                successProps={{
                                    description: cs.AML.amlQuestionaire.success,
                                }}
                            />
                        ),
                        stepName: "after-submit",
                    },
                ]}
            </FormMultiStepDrawer>
        </FormProvider>
    );
};

type IAMLFormStandaloneDrawer = {
    amlProps: IAMLDrawer;
    dialogProps?: DialogProps;
    open: boolean;
};

export const AMLFormStandaloneDrawer = ({ amlProps, dialogProps, open }: IAMLFormStandaloneDrawer) => {
    const { onCloseCallback, ...amlRest } = amlProps;
    return (
        <Modal open={open} onClose={onCloseCallback} {...dialogProps}>
            <AMLForm
                onCloseCallback={() => {
                    onCloseCallback?.();
                }}
                {...amlRest}
            />
        </Modal>
    );
};
